import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all'
      ? [...comics]
      : comics.filter((c) => c.categories.includes(activeSlug));

    switch (sortBy) {
      case 'popular':
        return result.sort((a, b) => b.views - a.views);
      case 'rating':
        return result.sort((a, b) => b.rating - a.rating);
      case 'latest':
      default:
        return result.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
    }
  }, [activeSlug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览', path: '/category/all' },
                ...(currentCategory && currentCategory.slug !== 'all'
                  ? [{ label: currentCategory.name }]
                  : []),
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              {currentCategory ? currentCategory.name + '漫画' : '全部分类'}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              汗汗漫画最新最全{currentCategory ? currentCategory.name : '韩漫'}资源，免费在线观看
            </p>

            <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{filteredComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1">
                {([
                  { value: 'latest', label: '最新' },
                  { value: 'popular', label: '人气' },
                  { value: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="small" />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无作品</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}